/**
 * App detail page component.
 * Only accessible in preview mode.
 * Apps are sandboxes with the type=app label.
 *
 * @module pages/AppDetailPage
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import {
  ArrowLeft,
  Trash2,
  Copy,
  Check,
  Camera,
  Terminal,
  ExternalLink,
  Info,
  Square,
  Play,
  RefreshCw,
} from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  LoadingPage,
  Badge,
  ConfirmModal,
  Modal,
  Input,
  LabelsEditor,
  getSandboxStateBadgeVariant,
  SlidePanel,
  EgressPolicyEditor,
} from '@/components/ui';
import type { LabelsEditorRef } from '@/components/ui';
import { SandboxTerminal, SandboxMonitor, FileExplorer } from '@/components/terminal';
import {
  useApp,
  useDeleteApp,
  useCreateAppSnapshot,
  useAddAppPort,
  useRemoveAppPort,
  useSetAppEgressPolicy,
  useStopApp,
  useResumeApp,
  useAppEgressDecisions,
} from '@/hooks/useApps';
import { usePreviewMode } from '@/hooks/usePreviewMode';
import { useConnections } from '@/hooks/useConnections';
import { ConnectionsSection } from '@/components/connectors';
import type {
  EgressHostRule,
  EgressPolicy,
  EgressPolicyAction,
  SandboxPort,
  EgressDecisionEntry,
  AppPortAuthConfig,
} from '@/types';

/**
 * Masks query string parameters in a URL for display (e.g., tokens).
 * The full URL is still used for href, but display shows ""
 */
function maskUrlQueryString(url: string): string {
  try {
    const urlObj = new URL(url);
    if (urlObj.search) {
      return `${urlObj.origin}${urlObj.pathname}`;
    }
    return url;
  } catch {
    return url;
  }
}

/**
 * App detail page
 */
export function AppDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: app, isLoading, error } = useApp(id!);
  const deleteMutation = useDeleteApp();
  const snapshotMutation = useCreateAppSnapshot();
  const addPortMutation = useAddAppPort();
  const removePortMutation = useRemoveAppPort();
  const setEgressPolicyMutation = useSetAppEgressPolicy();
  const stopMutation = useStopApp();
  const resumeMutation = useResumeApp();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [showAddPortModal, setShowAddPortModal] = useState(false);
  const [showEgressPolicyModal, setShowEgressPolicyModal] = useState(false);
  const [showTerminal, setShowTerminal] = useState(true);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [snapshotSuccess, setSnapshotSuccess] = useState(false);
  const [snapshotTimeTakenMs, setSnapshotTimeTakenMs] = useState<number>(0);
  const [snapshotLabels, setSnapshotLabels] = useState<Record<string, string>>({});
  const [newPortNumber, setNewPortNumber] = useState('');
  const [newPortName, setNewPortName] = useState('');
  const [portToRemove, setPortToRemove] = useState<SandboxPort | null>(null);

  // App-specific port options
  const [portAuthEnabled, setPortAuthEnabled] = useState(false);
  const [portAuthGitHubEmails, setPortAuthGitHubEmails] = useState('');

  // Ref for snapshot labels editor to get uncommitted draft
  const snapshotLabelsEditorRef = useRef<LabelsEditorRef>(null);

  const [egressDefaultAction, setEgressDefaultAction] = useState<EgressPolicyAction>('Allow');
  const [egressHostRules, setEgressHostRules] = useState<EgressHostRule[]>([]);
  const [egressTab, setEgressTab] = useState<'policy' | 'decisions'>('policy');

  const isPreviewMode = usePreviewMode();
  const { getConnectionName } = useConnections();
  const isRunning = app?.state?.toLowerCase() === 'running';
  const isStopped = app?.state?.toLowerCase() === 'stopped';

  // Fetch egress decisions when on the decisions tab and app is running
  const {
    data: egressDecisions,
    isLoading: isEgressDecisionsLoading,
    error: egressDecisionsError,
    refetch: refetchEgressDecisions,
    isFetching: isEgressDecisionsFetching,
  } = useAppEgressDecisions(id!);

  useEffect(() => {
    if (!showEgressPolicyModal) {
      return;
    }

    // Seed editor state from app's egress policy when opening.
    if (app?.egressPolicy) {
      setEgressDefaultAction(app.egressPolicy.defaultAction);
      setEgressHostRules(app.egressPolicy.hostRules ?? []);
    } else {
      setEgressDefaultAction('Allow');
      setEgressHostRules([]);
    }
  }, [showEgressPolicyModal, app]);

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate(id, {
        onSuccess: () => navigate('/apps'),
      });
    }
  };

  const handleSnapshot = () => {
    if (id) {
      // Include any uncommitted draft label from the labels editor
      const finalLabels = { ...snapshotLabels };
      const draft = snapshotLabelsEditorRef.current?.getDraft();
      if (draft) {
        finalLabels[draft.key] = draft.value;
      }

      const startTime = performance.now();
      snapshotMutation.mutate(
        { id, labels: finalLabels },
        {
          onSuccess: () => {
            const endTime = performance.now();
            setSnapshotTimeTakenMs(Math.round(endTime - startTime));
            setSnapshotSuccess(true);
            setShowSnapshotModal(false);
          },
        }
      );
    }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleAddPort = () => {
    const portNumber = parseInt(newPortNumber, 10);
    if (!portNumber || isNaN(portNumber) || portNumber < 1 || portNumber > 65535) {
      return;
    }
    if (id) {
      // Build auth config if enabled
      let auth: AppPortAuthConfig | undefined;
      if (portAuthEnabled && portAuthGitHubEmails.trim()) {
        const emails = portAuthGitHubEmails
          .split(',')
          .map((e) => e.trim())
          .filter((e) => e.length > 0);
        if (emails.length > 0) {
          auth = {
            enabled: true,
            github: {
              allowedUsers: emails,
            },
          };
        }
      }

      addPortMutation.mutate(
        {
          appId: id,
          port: portNumber,
          name: newPortName.trim() || undefined,
          activationMode: 'OnDemand',
          auth,
        },
        {
          onSuccess: () => {
            setShowAddPortModal(false);
            setNewPortNumber('');
            setNewPortName('');
            setPortAuthEnabled(false);
            setPortAuthGitHubEmails('');
          },
        }
      );
    }
  };

  const handleRemovePort = () => {
    if (id && portToRemove) {
      removePortMutation.mutate(
        {
          appId: id,
          port: portToRemove.port,
          name: portToRemove.name,
        },
        {
          onSuccess: () => {
            setPortToRemove(null);
          },
        }
      );
    }
  };

  const handleSaveEgressPolicy = () => {
    if (!id) {
      return;
    }

    const trimmedRules: EgressHostRule[] = egressHostRules.map((r) => ({
      pattern: r.pattern.trim(),
      action: r.action,
    }));

    if (trimmedRules.some((r) => !r.pattern)) {
      return;
    }

    const policyToSave: EgressPolicy = {
      defaultAction: egressDefaultAction,
      hostRules: trimmedRules.map((r) => ({
        pattern: r.pattern,
        ...(r.action ? { action: r.action } : {}),
      })),
    };

    setEgressPolicyMutation.mutate(
      { appId: id, policy: policyToSave },
      {
        onSuccess: () => {
          setShowEgressPolicyModal(false);
        },
      }
    );
  };

  const effectiveEgressPolicy = app?.egressPolicy;
  const egressPolicyDisplayDefault = effectiveEgressPolicy?.defaultAction ?? 'Allow';
  const egressPolicyDisplayRules = effectiveEgressPolicy?.hostRules ?? [];

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error || !app) {
    return (
      <div>
        <PageHeader
          title="App Not Found"
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Apps', href: '/apps' },
            { label: 'Not Found' },
          ]}
        />
        <Card>
          <CardBody>
            <p className="text-gray-500">The requested app could not be found.</p>
            <Link to="/apps">
              <Button
                variant="secondary"
                className="mt-4"
                leftIcon={<ArrowLeft className="h-4 w-4" />}
              >
                Back to Apps
              </Button>
            </Link>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {app.id}
            {app.state && (
              <Badge variant={getSandboxStateBadgeVariant(app.state)}>{app.state}</Badge>
            )}
          </span>
        }
        description={`Based on ${app.sourcesRef?.diskImage?.id || 'Unknown'}`}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Apps', href: '/apps' },
          { label: app.id },
        ]}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/apps">
              <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
                Back
              </Button>
            </Link>
            <Button
              variant="secondary"
              leftIcon={<Info className="h-4 w-4" />}
              onClick={() => setShowInfoPanel(true)}
            >
              Details
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Terminal className="h-4 w-4" />}
              onClick={() => setShowTerminal(!showTerminal)}
              disabled={!isRunning}
              title={!isRunning ? 'App must be running to open terminal' : undefined}
            >
              {showTerminal ? 'Hide Terminal' : 'Open Terminal'}
            </Button>
            <Button
              variant="secondary"
              leftIcon={<Camera className="h-4 w-4" />}
              onClick={() => setShowSnapshotModal(true)}
              disabled={!isRunning}
              title={!isRunning ? 'App must be running to create snapshot' : undefined}
            >
              Snapshot
            </Button>
            {isStopped ? (
              <Button
                variant="secondary"
                leftIcon={<Play className="h-4 w-4" />}
                onClick={() => resumeMutation.mutate(id!)}
                disabled={resumeMutation.isPending}
              >
                {resumeMutation.isPending ? 'Resuming...' : 'Resume'}
              </Button>
            ) : (
              <Button
                variant="secondary"
                leftIcon={<Square className="h-4 w-4" />}
                onClick={() => stopMutation.mutate(id!)}
                disabled={stopMutation.isPending || !isRunning}
                title={!isRunning ? 'App must be running to stop' : undefined}
              >
                {stopMutation.isPending ? 'Stopping...' : 'Stop'}
              </Button>
            )}
            <Button
              variant="danger"
              leftIcon={<Trash2 className="h-4 w-4" />}
              onClick={() => setShowDeleteModal(true)}
            >
              Delete
            </Button>
          </div>
        }
      />

      {/* Terminal */}
      {showTerminal && (
        <div className="mb-6">
          <Card className="h-[500px]">
            <SandboxTerminal sandboxId={id!} onClose={() => setShowTerminal(false)} />
          </Card>
        </div>
      )}

      {/* Stats and Processes - shown when app is running */}
      {isRunning && (
        <div className="mb-6">
          <SandboxMonitor sandboxId={id!} />
        </div>
      )}

      {/* File Explorer - always shown when app is running */}
      {isRunning && (
        <div className="mb-6">
          <Card className="h-[400px]">
            <FileExplorer sandboxId={id!} />
          </Card>
        </div>
      )}

      {/* Snapshot Success Message */}
      {snapshotSuccess && (
        <div className="mb-6">
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-full">
                    <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Snapshot Created Successfully
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Time taken: {snapshotTimeTakenMs.toLocaleString()} ms
                    </p>
                  </div>
                </div>
                <Link to="/snapshots">
                  <Button variant="secondary" size="sm">
                    View Snapshots
                  </Button>
                </Link>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* App URL */}
        <Card className="lg:col-span-2">
          <CardHeader title="App URL" />
          <CardBody>
            {app.appUrl ? (
              <div className="space-y-3">
                {!isRunning && (
                  <p className="text-sm text-blue-600 dark:text-blue-400 mb-2">
                    💡 App will start on-demand when you access the URL.
                  </p>
                )}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <a
                    href={app.appUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    <span className="font-mono text-sm break-all">
                      {maskUrlQueryString(app.appUrl)}
                    </span>
                    <ExternalLink className="h-4 w-4 flex-shrink-0" />
                  </a>
                  <button
                    onClick={() => handleCopy(app.appUrl!, 'app-url')}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Copy URL"
                  >
                    {copiedField === 'app-url' ? (
                      <Check className="h-4 w-4 text-success-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  App URL will be available once the app domain is configured.
                </p>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Connections Section - Preview Only */}
        {isPreviewMode && (
          <ConnectionsSection
            allowedEntries={egressDecisions?.networkEgress?.allowed ?? []}
            deniedEntries={egressDecisions?.networkEgress?.denied ?? []}
            isLoading={isEgressDecisionsLoading}
          />
        )}

        {/* Network Egress */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Network Egress"
            action={
              egressTab === 'policy' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowEgressPolicyModal(true)}
                  disabled={!isRunning}
                  title={!isRunning ? 'App must be running to update egress policy' : undefined}
                >
                  Edit
                </Button>
              )
            }
          />
          <CardBody>
            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
              <button
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  egressTab === 'policy'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                onClick={() => setEgressTab('policy')}
              >
                Policy
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  egressTab === 'decisions'
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                onClick={() => setEgressTab('decisions')}
              >
                Audit Log
              </button>
            </div>

            {/* Policy Tab */}
            {egressTab === 'policy' && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Default:
                  </span>
                  <Badge variant={egressPolicyDisplayDefault === 'Allow' ? 'success' : 'danger'}>
                    {egressPolicyDisplayDefault}
                  </Badge>
                </div>

                {egressPolicyDisplayRules.length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Host rules
                    </div>
                    {egressPolicyDisplayRules.map((rule, idx) => {
                      const action = rule.action ?? egressPolicyDisplayDefault;
                      return (
                        <div
                          key={`${rule.pattern}-${idx}`}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div className="flex flex-col">
                            <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                              {rule.pattern}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Action: {action}
                              {rule.action ? '' : ' (default)'}
                            </span>
                          </div>
                          <Badge variant={action === 'Allow' ? 'success' : 'danger'}>
                            {action}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No host-specific rules configured.
                  </p>
                )}

                {!isRunning && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Editing is only available when the app is running.
                  </p>
                )}
              </div>
            )}

            {/* Decisions/Audit Log Tab */}
            {egressTab === 'decisions' && (
              <div className="space-y-4">
                {!isRunning ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Audit log is only available when the app is running.
                  </p>
                ) : isEgressDecisionsLoading ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading audit log...</p>
                ) : egressDecisionsError ? (
                  <p className="text-sm text-danger-600 dark:text-danger-400">
                    Failed to load audit log.
                  </p>
                ) : (
                  <>
                    {/* Refresh Button */}
                    <div className="flex items-center justify-between">
                      {egressDecisions?.lastUpdated && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Last updated: {new Date(egressDecisions.lastUpdated).toLocaleString()}
                        </p>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={
                          <RefreshCw
                            className={`h-4 w-4 ${isEgressDecisionsFetching ? 'animate-spin' : ''}`}
                          />
                        }
                        onClick={() => refetchEgressDecisions()}
                        disabled={isEgressDecisionsFetching}
                      >
                        {isEgressDecisionsFetching ? 'Refreshing...' : 'Refresh'}
                      </Button>
                    </div>

                    {/* Denied Requests */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Denied Requests
                        </span>
                        <Badge variant="danger">
                          {egressDecisions?.networkEgress?.denied?.length ?? 0}
                        </Badge>
                      </div>
                      {(egressDecisions?.networkEgress?.denied?.length ?? 0) > 0 ? (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {egressDecisions!.networkEgress.denied.map(
                            (entry: EgressDecisionEntry, idx: number) => (
                              <div
                                key={`denied-${idx}`}
                                className="flex items-center justify-between p-2 bg-danger-50 dark:bg-danger-900/20 rounded text-sm"
                              >
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-danger-700 dark:text-danger-300">
                                      {entry.method ?? 'GET'} {entry.scheme ?? 'https'}://
                                      {entry.host}
                                      {entry.path ?? ''}
                                    </span>
                                    {entry.connectionId && (
                                      <Badge variant="info" className="text-xs">
                                        Connector
                                      </Badge>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(entry.timestamp).toLocaleString()}
                                    {entry.connectionId &&
                                      ` • ${getConnectionName(entry.connectionId) ?? entry.connectionId.substring(0, 8) + '...'}`}
                                  </span>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No denied requests.
                        </p>
                      )}
                    </div>

                    {/* Allowed Requests */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Allowed Requests
                        </span>
                        <Badge variant="success">
                          {egressDecisions?.networkEgress?.allowed?.length ?? 0}
                        </Badge>
                      </div>
                      {(egressDecisions?.networkEgress?.allowed?.length ?? 0) > 0 ? (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {egressDecisions!.networkEgress.allowed.map(
                            (entry: EgressDecisionEntry, idx: number) => (
                              <div
                                key={`allowed-${idx}`}
                                className="flex items-center justify-between p-2 bg-success-50 dark:bg-success-900/20 rounded text-sm"
                              >
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-success-700 dark:text-success-300">
                                      {entry.method ?? 'GET'} {entry.scheme ?? 'https'}://
                                      {entry.host}
                                      {entry.path ?? ''}
                                    </span>
                                    {entry.connectionId && (
                                      <Badge variant="info" className="text-xs">
                                        Connector
                                      </Badge>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {new Date(entry.timestamp).toLocaleString()}
                                    {entry.connectionId &&
                                      ` • ${getConnectionName(entry.connectionId) ?? entry.connectionId.substring(0, 8) + '...'}`}
                                  </span>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No allowed requests recorded.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete App"
        message={`Are you sure you want to delete app "${app.id}"? This action cannot be undone and any data will be lost.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Snapshot Modal */}
      <Modal
        isOpen={showSnapshotModal}
        onClose={() => setShowSnapshotModal(false)}
        title="Create Snapshot"
        description="Create a snapshot of this app."
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowSnapshotModal(false)}
              disabled={snapshotMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSnapshot} isLoading={snapshotMutation.isPending} disabled={!id}>
              Create
            </Button>
          </>
        }
      >
        <LabelsEditor
          ref={snapshotLabelsEditorRef}
          value={snapshotLabels}
          onChange={setSnapshotLabels}
          helperText="Optional key/value labels to attach to the snapshot."
          requireValue={false}
        />
      </Modal>

      {/* Add Port Modal - Enhanced for Apps with auth */}
      <Modal
        isOpen={showAddPortModal}
        onClose={() => {
          setShowAddPortModal(false);
          setNewPortNumber('');
          setNewPortName('');
          setPortAuthEnabled(false);
          setPortAuthGitHubEmails('');
        }}
        title="Expose Port"
        description="Expose a port from your app to get an HTTPS URL for external access."
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddPortModal(false);
                setNewPortNumber('');
                setNewPortName('');
                setPortAuthEnabled(false);
                setPortAuthGitHubEmails('');
              }}
              disabled={addPortMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddPort}
              isLoading={addPortMutation.isPending}
              disabled={
                !newPortNumber ||
                parseInt(newPortNumber, 10) < 1 ||
                parseInt(newPortNumber, 10) > 65535
              }
            >
              Add Port
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Port Number"
            type="number"
            placeholder="8080"
            value={newPortNumber}
            onChange={(e) => setNewPortNumber(e.target.value)}
            min={1}
            max={65535}
            required
            helperText="The port number your service is listening on (1-65535)"
          />
          <Input
            label="Name (Optional)"
            placeholder="my-service"
            value={newPortName}
            onChange={(e) => setNewPortName(e.target.value)}
            helperText="A friendly name for this port (must be unique)"
          />

          {/* Authentication */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Authentication
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={portAuthEnabled}
                  onChange={(e) => setPortAuthEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                <span className="ms-2 text-sm text-gray-500 dark:text-gray-400">
                  {portAuthEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>

            {portAuthEnabled && (
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>GitHub Authentication</strong>
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Only users with the specified GitHub email addresses will be able to access this
                    port.
                  </p>
                </div>
                <Input
                  label="Allowed GitHub Emails"
                  placeholder="user1@example.com, user2@example.com"
                  value={portAuthGitHubEmails}
                  onChange={(e) => setPortAuthGitHubEmails(e.target.value)}
                  helperText="Enter a comma-separated list of GitHub email addresses that are allowed to access this port"
                />
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Egress Policy Modal */}
      <Modal
        isOpen={showEgressPolicyModal}
        onClose={() => setShowEgressPolicyModal(false)}
        title="Network Egress Policy"
        description="Control outbound network access for this app. Changes apply only while the app is running."
        size="lg"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowEgressPolicyModal(false)}
              disabled={setEgressPolicyMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEgressPolicy}
              isLoading={setEgressPolicyMutation.isPending}
              disabled={!isRunning || egressHostRules.some((r) => !r.pattern || !r.pattern.trim())}
            >
              Save
            </Button>
          </>
        }
      >
        <EgressPolicyEditor
          defaultAction={egressDefaultAction}
          onDefaultActionChange={setEgressDefaultAction}
          hostRules={egressHostRules}
          onHostRulesChange={setEgressHostRules}
        />
      </Modal>

      {/* Remove Port Confirmation Modal */}
      <ConfirmModal
        isOpen={!!portToRemove}
        onClose={() => setPortToRemove(null)}
        onConfirm={handleRemovePort}
        title="Remove Port"
        message={`Are you sure you want to remove port ${portToRemove?.name ? `"${portToRemove.name}" (${portToRemove.port})` : portToRemove?.port}? The external URL will no longer be accessible.`}
        confirmText="Remove"
        variant="danger"
        isLoading={removePortMutation.isPending}
      />

      {/* Info Slide Panel */}
      <SlidePanel
        isOpen={showInfoPanel}
        onClose={() => setShowInfoPanel(false)}
        title="App Details"
        description="Basic information and resource allocation"
        size="md"
      >
        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
              Basic Information
            </h4>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">App ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200 flex items-center gap-2">
                  <span className="font-mono">{app.id}</span>
                  <button
                    onClick={() => handleCopy(app.id, 'id')}
                    className="text-gray-400 hover:text-gray-600 dark:text-gray-200 dark:hover:text-gray-100"
                    aria-label="Copy App ID"
                  >
                    {copiedField === 'id' ? (
                      <Check className="h-4 w-4 text-success-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Labels</dt>
                <dd className="mt-1">
                  {app.labels && Object.keys(app.labels).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(app.labels as Record<string, string>).map(([key, value]) => (
                        <span
                          key={key}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200"
                        >
                          {key}: {value}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Source Disk Image
                </dt>
                <dd className="mt-1">
                  {app.sourcesRef?.diskImage?.id ? (
                    <Link
                      to={`/disk-images/${encodeURIComponent(app.sourcesRef.diskImage.id)}`}
                      className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
                    >
                      {app.sourcesRef.diskImage.id}
                    </Link>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Resources */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Resources</h4>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CPU</dt>
                <dd className="mt-1">
                  <Badge variant="info">{app.resources?.cpu || '-'}</Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Memory</dt>
                <dd className="mt-1">
                  <Badge variant="info">{app.resources?.memory || '-'}</Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Disk</dt>
                <dd className="mt-1">
                  <Badge variant="neutral">{app.resources?.disk || '-'}</Badge>
                </dd>
              </div>
              {app.resources?.gpu && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">GPU</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-200">
                    {app.resources.gpu.sku} x{app.resources.gpu.quantity}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
}
